import React, { FC, useRef, useState, DragEvent, ChangeEvent } from 'react';
import { createPropertyComments } from '../../../../api/Property/createPropertyComments';
import { updatePropertyComments } from '../../../../api/Property/updatePropertyComments';
import { convertHeicToJpeg } from '../../../../utility/utility_functions';
import PhotoHistoryList from './PhotoHistoryList';
import PhotoGalleryViewer from './PhotoGalleryViewer';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB, matches backend limit

type PropertyPhotosSectionProps = {
  propertyId: string;
  commentData: any;
  refetchCommentData: () => void;
}

const PropertyPhotosSection: FC<PropertyPhotosSectionProps> = ({ propertyId, commentData, refetchCommentData }) => {
  const [ isDragging, setIsDragging ] = useState(false);
  const [ isUploading, setIsUploading ] = useState(false);
  const [ uploadProgress, setUploadProgress ] = useState(0);
  const [ galleryIndex, setGalleryIndex ] = useState<number | null>(null);
  const [ uploadError, setUploadError ] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photos = commentData?.comment?.photos || [];

  const uploadPhotos = async (files: File[]) => {
    if (!files.length) return;

    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      setUploadError(`${oversizedFiles.length === 1 ? 'This photo is' : 'These photos are'} over the 5MB limit: ${oversizedFiles.map(file => file.name).join(', ')}`);
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    setUploadProgress(0);

    const processed: File[] = [];
    for (const file of files) {
      if (file.type === 'image/heic') {
        const resized = await convertHeicToJpeg(file);
        if (resized instanceof File) processed.push(resized);
      } else {
        processed.push(file);
      }
    }

    const body = new FormData();
    processed.forEach(file => body.append('comment[photos][]', file));

    const config = {
      onUploadProgress: (progressEvent: any) => {
        const total = progressEvent.total || 0;
        const percent = total > 0 ? Math.round((progressEvent.loaded * 100) / total) : 0;
        setUploadProgress(percent);
      }
    };

    try {
      if (!commentData?.comment?.id) {
        body.append('comment[title]', '');
        body.append('comment[notes]', '');
        await createPropertyComments(propertyId, body, config);
      } else {
        await updatePropertyComments(propertyId, commentData.comment.id, body, config);
      }
      await refetchCommentData();
    } catch (error) {
      console.error('Error uploading photos:', error);
      setUploadError('Something went wrong uploading your photos. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    await uploadPhotos(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      await uploadPhotos(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleRemovePhoto = async (photoId: string) => {
    if (!commentData?.comment?.id) return;
    const body = new FormData();
    body.append('comment[remove_photos][]', photoId);
    try {
      await updatePropertyComments(propertyId, commentData.comment.id, body);
      await refetchCommentData();
    } catch (error) {
      console.error('Error removing photo:', error);
    }
  };

  return (
    <div className='mb-6'>
      <div className='flex items-center justify-between mb-2'>
        <h3 className='text-lg font-semibold text-gray-900'>Property Photos</h3>
        {photos.length > 0 && (
          <button
            type='button'
            onClick={() => setGalleryIndex(0)}
            className='rounded-md px-3 py-1.5 text-sm font-semibold text-indigo-600 border border-indigo-600 hover:bg-indigo-50'
          >
            View Full Size
          </button>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'}`}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='.heic,.png,.jpg,.jpeg,.webp'
          multiple
          className='hidden'
          onChange={handleFileSelect}
        />
        <p className='text-gray-700'>Drag &amp; drop photos here</p>
        <p className='text-gray-400 text-sm mt-1'>{isUploading ? 'Uploading...' : 'or click to browse files'}</p>
      </div>

      {uploadError && (
        <p className='text-xs text-red-600 mt-2'>{uploadError}</p>
      )}

      {isUploading && (
        <div className='mt-2'>
          <div className='w-full bg-gray-200 rounded-full h-2 overflow-hidden'>
            <div
              className='bg-indigo-600 h-2 transition-all duration-150'
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className='text-xs text-gray-500 mt-1'>{uploadProgress}% uploaded</p>
        </div>
      )}

      <PhotoHistoryList
        photos={photos}
        onSelectPhoto={(index) => setGalleryIndex(index)}
        onRemovePhoto={handleRemovePhoto}
      />

      <PhotoGalleryViewer
        photos={photos}
        activeIndex={galleryIndex ?? 0}
        visible={galleryIndex !== null}
        onHide={() => setGalleryIndex(null)}
        onActiveIndexChange={setGalleryIndex}
      />
    </div>
  );
};

export default PropertyPhotosSection;
